import { useState } from 'react'
import {
  KpiStrip, MetricCard, Panel, SectionHeader, ErpTable, Th, Td, EmptyRow,
} from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { Modal, Field } from '@/shared/ui/Modal'
import {
  approveBudget, awardReasons, createIncentiveBudget, deliveryChannels, incentiveKinds,
  listAwards, listCellMembers, listCells, listIncentiveBudgets, recipientKinds,
  recordAward, reversalReasons, reverseAward, budgetPurposes, valueBases,
  type IncentiveAward, type IncentiveBudget,
} from './api'
import { Donut, type DonutSlice } from '@/shared/charts/Donut'
import { ElectionPicker } from './ElectionPicker'
import { useElection } from './useElection'
import { label } from './vocab'
import { DatePicker } from '@/components/erp/date-picker'

/**
 * Incentives — §16.7.
 *
 * 1. Budgets — cap, committed, remaining, status; create and approve.
 * 2. Awards — kind, reason, recipient, value, channel, delivered date; reversal.
 * 3. Spend — the totals across every budget.
 *
 * ## Two problems
 *
 * The budget loader's dependency array was `[]`. `electionId` is `null` on first render,
 * so the loader ran once against `Promise.resolve([])` and never re-ran — the screen was
 * empty regardless of what the server held, and switching election did nothing.
 *
 * Every write the server offers was unreachable. `createIncentiveBudget` and `recordAward`
 * had no client function at all; `approveBudget` and `reverseAward` existed in `api.ts`
 * and nothing imported them — and `reverseAward` sent `{reason}` where
 * `AwardReversalRequest` declares `{reversesId, reversalReasonCode, note}`, so it would
 * have 400'd on validation had anything called it.
 *
 * ## Rule 2.4, which is not negotiable here
 *
 * **A control the caller might be allowed to use is offered, and the server's refusal is
 * what speaks.** Approve and Record are rendered for anyone who can read this screen. A
 * caller without `turnout:incentive-manage` gets a 403 carrying a sentence that names the
 * authority; a control that quietly is not there teaches nobody anything. The same rule
 * governs the cap: an award past it is refused with 409
 * `turnout/budget-cap-exceeded`, and that sentence is shown rather than pre-empted.
 */
export function IncentivesPage() {
  const canRead = useCan('turnout:incentive-read')
  const selection = useElection()
  const { electionId } = selection
  const [creating, setCreating] = useState(false)
  const [awarding, setAwarding] = useState<IncentiveBudget | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /*
     Recording an award reloaded the BUDGET list — which is where the committed and
     remaining figures live — and nothing told the awards table underneath it that a row
     had appeared. It sat there stale until the page was reloaded by hand. Bumping this
     is what the awards panels watch.
  */
  const [awardsVersion, setAwardsVersion] = useState(0)
  const refreshAwards = () => setAwardsVersion((v) => v + 1)

  const budgets = useLoad(
    () => (electionId ? listIncentiveBudgets(electionId) : Promise.resolve([])),
    [electionId])

  // Forbidden — offered and refused, not hidden.
  if (!canRead) return <Forbidden what="Incentives" />

  const budgetRows = budgets.data ?? []
  const num = (v: string | number | null | undefined) => Number(v ?? 0)
  const sum = (pick: (b: IncentiveBudget) => string | number) =>
    budgetRows.reduce((s, b) => s + num(pick(b)), 0)

  const approve = async (budget: IncentiveBudget) => {
    setBusy(true); setRowError(null)
    try { await approveBudget(budget.id); budgets.reload() }
    catch (e) { setRowError(explain(e)) }
    finally { setBusy(false) }
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Incentives"
        description="Coordinator support, cell operations and accessibility budgets — spend that can be seen in one place."
        right={(
          <span className="flex items-center gap-2">
            <ElectionPicker selection={selection} />
            <button type="button" className="erp-btn erp-btn-primary h-9"
              disabled={!electionId} onClick={() => { setRowError(null); setCreating(true) }}>
              New budget
            </button>
          </span>
        )}
      />
      <ErrorStrip message={rowError ?? selection.error ?? budgets.error} />

      {budgets.loading && budgetRows.length === 0
        ? <LoadingBlock label="Loading budgets…" /> : (
        <>
          <KpiStrip>
            <MetricCard label="Total cap"
              value={budgetRows.length === 0 ? '—' : sum((b) => b.capAmount).toLocaleString()}
              sub="across all budgets" />
            <MetricCard label="Committed"
              value={budgetRows.length === 0 ? '—' : sum((b) => b.committedAmount).toLocaleString()}
              sub="awards recorded" />
            <MetricCard label="Remaining"
              value={budgetRows.length === 0 ? '—' : sum((b) => b.remainingAmount).toLocaleString()}
              sub="available across all budgets" accent />
          </KpiStrip>

          {/* Panel 1 — Budgets */}
          <Panel title="Budgets"
            description="Cap vs committed. A budget must be ACTIVE before an award can be recorded against it.">
            <ErpTable head={(
              <>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Purpose</Th>
                <Th className="text-right">Cap</Th>
                <Th className="text-right">Committed</Th>
                <Th className="text-right">Remaining</Th>
                <Th>Status</Th>
                <Th>Currency</Th>
                <Th />
              </>
            )}>
              {budgetRows.length === 0 ? (
                <EmptyRow colSpan={9}
                  message="No budgets. Create one — it starts as DRAFT and must be approved before awards can be recorded." />
              ) : budgetRows.map((b) => (
                <tr key={b.id} className="hover:bg-muted/40">
                  <Td className="font-mono text-erp-xs">{b.code}</Td>
                  <Td>{b.name}</Td>
                  <Td className="text-muted-foreground">{label(b.purposeCode)}</Td>
                  <Td className="text-right tabular-nums">{num(b.capAmount).toLocaleString()}</Td>
                  <Td className="text-right tabular-nums">
                    {num(b.committedAmount).toLocaleString()}
                  </Td>
                  <Td className="text-right tabular-nums">
                    {num(b.remainingAmount) === 0
                      ? <span className="text-red-700 font-medium">0 — Exhausted</span>
                      : num(b.remainingAmount).toLocaleString()}
                  </Td>
                  <Td className="text-muted-foreground">{label(b.statusCode)}</Td>
                  <Td className="text-muted-foreground">{b.currency}</Td>
                  <Td className="text-right whitespace-nowrap">
                    {b.statusCode === 'DRAFT' && (
                      <button type="button" className="erp-btn h-8 mr-1"
                        disabled={busy} onClick={() => approve(b)}>
                        Approve
                      </button>
                    )}
                    {b.statusCode === 'ACTIVE' && (
                      <button type="button" className="erp-btn erp-btn-primary h-8"
                        disabled={busy}
                        onClick={() => { setRowError(null); setAwarding(b) }}>
                        Record award
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </ErpTable>
          </Panel>

          {/* Panel 2 — Awards, one panel per budget. Every budget, not just the first:
              showing awards for budgets[0] alone hid the spend on every other one. */}
          {budgetRows.map((b) => (
            <AwardsPanel key={b.id} budget={b} version={awardsVersion}
              onChanged={() => { budgets.reload(); refreshAwards() }} />
          ))}
        </>
      )}

      {creating && electionId && (
        <BudgetModal
          electionId={electionId}
          onClose={() => setCreating(false)}
          onDone={() => { setCreating(false); budgets.reload() }}
        />
      )}
      {awarding && (
        <AwardModal
          budget={awarding}
          onClose={() => setAwarding(null)}
          onDone={() => { setAwarding(null); budgets.reload(); refreshAwards() }}
        />
      )}
    </div>
  )
}

function AwardsPanel({ budget, version, onChanged }: {
  budget: IncentiveBudget
  /** Bumped by the page when an award is written, so this table refetches. */
  version: number
  onChanged: () => void
}) {
  const [cellFilter, setCellFilter] = useState('')
  const [memberFilter, setMemberFilter] = useState('')
  const awards = useLoad(
    () => listAwards(budget.id, {
      cellId: cellFilter || undefined, memberId: memberFilter || undefined }),
    [budget.id, cellFilter, memberFilter, version])
  const cells = useLoad(
    () => listCells({ electionId: budget.electionId }), [budget.electionId])
  const members = useLoad(
    () => (cellFilter ? listCellMembers(cellFilter) : Promise.resolve([])), [cellFilter])
  const [reversing, setReversing] = useState<IncentiveAward | null>(null)
  const rows = awards.data ?? []

  /*
     Net, not gross. A reversal is a row with a NEGATIVE amount, so summing every row is
     the correct arithmetic and a filter that hides reversals would overstate the spend.
  */
  const net = rows.reduce((sum, a) => sum + Number(a.amount), 0)
  const byCell = groupSpend(rows, (a) => a.cellId, (a) => a.cellName)
  const byMember = groupSpend(
    rows.filter((a) => a.memberId), (a) => a.memberId, (a) => a.memberDisplayName)

  return (
    <Panel title={`Awards — ${budget.name}`}
      description="A reversal writes a NEW row with a negative amount. Nothing here deletes an award."
      right={(
        <span className="flex items-center gap-2">
          <select className="erp-input h-8 text-erp-xs" value={cellFilter}
            aria-label="Filter awards by cell"
            disabled={cells.loading}
            onChange={(e) => { setCellFilter(e.target.value); setMemberFilter('') }}>
            <option value="">All cells</option>
            {(cells.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.label ?? c.code}</option>
            ))}
          </select>
          <select className="erp-input h-8 text-erp-xs" value={memberFilter}
            aria-label="Filter awards by member"
            disabled={!cellFilter || members.loading}
            onChange={(e) => setMemberFilter(e.target.value)}>
            <option value="">All members</option>
            {(members.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>{m.displayName}</option>
            ))}
          </select>
          {(cellFilter || memberFilter) && (
            <button type="button" className="erp-btn h-8"
              onClick={() => { setCellFilter(''); setMemberFilter('') }}>
              Clear
            </button>
          )}
        </span>
      )}>
      <ErrorStrip message={awards.error} />

      {rows.length > 0 && (
        <div className="grid gap-3 mb-4"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
          <Panel title="Spend per cell"
            description={cellFilter || memberFilter
              ? 'Within the current filter.'
              : 'Net of reversals, across this budget.'}>
            <Donut slices={byCell} total={net}
              formatValue={(v) => `${budget.currency} ${Math.round(v).toLocaleString()}`}
              emptyMessage="Nothing to chart — the net spend in this view is zero." />
          </Panel>
          <Panel title="Spend per member"
            description="Awards recorded against a named person.">
            <Donut slices={byMember}
              total={byMember.reduce((sum, x) => sum + x.value, 0)}
              formatValue={(v) => `${budget.currency} ${Math.round(v).toLocaleString()}`}
              emptyMessage="No award in this view names an individual member." />
          </Panel>
        </div>
      )}
      {awards.loading && rows.length === 0 ? <LoadingBlock label="Loading awards…" /> : (
        <ErpTable head={(
          <>
            <Th>Kind</Th>
            <Th>Reason</Th>
            <Th>Cell</Th>
            <Th>Member</Th>
            <Th className="text-right">Qty × unit</Th>
            <Th className="text-right">Amount</Th>
            <Th>Channel</Th>
            <Th>Delivered</Th>
            <Th>Type</Th>
            <Th />
          </>
        )}>
          {rows.length === 0 ? (
            <EmptyRow colSpan={10} message={cellFilter || memberFilter
              ? 'No awards match this filter.'
              : budget.statusCode === 'ACTIVE'
                ? 'No awards against this budget yet.'
                : `No awards. This budget is ${label(budget.statusCode)} — it must be ACTIVE before an award can be recorded.`} />
          ) : rows.map((a) => {
            const reversal = a.entryTypeCode === 'REVERSAL'
            const alreadyReversed = rows.some((r) => r.reversesId === a.id)
            return (
              <tr key={a.id} className="hover:bg-muted/40">
                <Td>{label(a.incentiveKindCode)}</Td>
                <Td className="text-muted-foreground">{label(a.awardReasonCode)}</Td>
                <Td>{a.cellName ?? <span className="text-muted-foreground">—</span>}</Td>
                <Td>
                  {a.memberDisplayName ?? (
                    <span className="text-muted-foreground">
                      {a.recipientKindCode === 'CELL' ? 'The cell' : '—'}
                    </span>
                  )}
                </Td>
                {/* The working, not just the answer: 7 × 2,300 is checkable at a glance
                    and an amount on its own is not. */}
                <Td className="text-right tabular-nums text-muted-foreground whitespace-nowrap">
                  {Number(a.quantity).toLocaleString()} × {Number(a.unitValue).toLocaleString()}
                </Td>
                <Td className="text-right tabular-nums whitespace-nowrap">
                  {reversal
                    ? <span className="text-red-700">
                        {Number(a.amount).toLocaleString()} {a.currency}
                      </span>
                    : `${Number(a.amount).toLocaleString()} ${a.currency}`}
                </Td>
                <Td className="text-muted-foreground">{label(a.deliveryChannelCode)}</Td>
                <Td className="text-muted-foreground">{a.deliveredOn}</Td>
                <Td>
                  {reversal
                    ? <span className="text-red-700 font-medium">Reversal</span>
                    : 'Award'}
                </Td>
                <Td className="text-right">
                  {!reversal && !alreadyReversed && (
                    <button type="button" className="erp-btn h-8"
                      onClick={() => setReversing(a)}>
                      Reverse
                    </button>
                  )}
                </Td>
              </tr>
            )
          })}
        </ErpTable>
      )}
      {reversing && (
        <ReverseModal
          award={reversing}
          onClose={() => setReversing(null)}
          onDone={() => { setReversing(null); awards.reload(); onChanged() }}
        />
      )}
    </Panel>
  )
}

/**
 * Sum award amounts by a key, keeping each group's own identity.
 *
 * <p>Net of reversals: a reversal is a negative row against the same key, so a group whose
 * every award was reversed sums to zero and correctly disappears from the chart.</p>
 */
function groupSpend(
  rows: IncentiveAward[],
  keyOf: (a: IncentiveAward) => string | null,
  nameOf: (a: IncentiveAward) => string | null,
): DonutSlice[] {
  const byKey = new Map<string, DonutSlice>()
  rows.forEach((a) => {
    const key = keyOf(a)
    if (!key) return
    const held = byKey.get(key)
    if (held) held.value += Number(a.amount)
    else byKey.set(key, { id: key, label: nameOf(a) ?? key.slice(0, 8), value: Number(a.amount) })
  })
  return [...byKey.values()]
}

/** A new budget starts DRAFT — creating one commits no money. */
function BudgetModal({ electionId, onClose, onDone }: {
  electionId: string
  onClose: () => void
  onDone: () => void
}) {
  const purposes = useLoad(budgetPurposes, [])
  const [form, setForm] = useState({
    code: '', name: '', purposeCode: '', currency: 'KES', capAmount: '', note: '',
  })
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const set = (k: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [k]: v }))

  const submit = async () => {
    setBusy(true); setError(null)
    try {
      await createIncentiveBudget(electionId, {
        electionId,
        code: form.code, name: form.name, purposeCode: form.purposeCode,
        currency: form.currency, capAmount: Number(form.capAmount),
        // Deliberately omitted rather than filled with a placeholder. The column is
        // nullable, and null means "not scoped to a unit" — which is the truth for a
        // budget the form does not ask a unit for. 'KE' would have claimed national scope.
        note: form.note || null,
      })
      onDone()
    } catch (e) { setError(explain(e)); setBusy(false) }
  }

  const ready = form.code && form.name && form.purposeCode && form.capAmount

  return (
    <Modal open onClose={onClose} title="New incentive budget"
      description="Starts as DRAFT. No award can be recorded until it is approved."
      footer={(
        <>
          <button type="button" className="erp-btn h-9" onClick={onClose}>Cancel</button>
          <button type="button" className="erp-btn erp-btn-primary h-9"
            disabled={busy || !ready} onClick={submit}>
            {busy ? 'Creating…' : 'Create'}
          </button>
        </>
      )}>
      <ErrorStrip message={error} />
      <div className="space-y-3">
        <Field label="Code" required hint="Short, unique within the election.">
          <input className="erp-input h-9 w-full" value={form.code}
            onChange={(e) => set('code')(e.target.value)} />
        </Field>
        <Field label="Name" required>
          <input className="erp-input h-9 w-full" value={form.name}
            onChange={(e) => set('name')(e.target.value)} />
        </Field>
        <Field label="Purpose" required>
          <select className="erp-input h-9 w-full" value={form.purposeCode}
            disabled={purposes.loading}
            onChange={(e) => set('purposeCode')(e.target.value)}>
            <option value="">Choose…</option>
            {(purposes.data ?? []).map((p) => (
              <option key={p} value={p}>{label(p)}</option>
            ))}
          </select>
        </Field>
        <Field label="Cap" required hint="The ceiling. An award past it is refused by the server.">
          <input className="erp-input h-9 w-full" type="number" min="0" value={form.capAmount}
            onChange={(e) => set('capAmount')(e.target.value)} />
        </Field>
        <Field label="Currency" required>
          <input className="erp-input h-9 w-full" value={form.currency} maxLength={3}
            onChange={(e) => set('currency')(e.target.value.toUpperCase())} />
        </Field>
        <Field label="Note">
          <textarea className="erp-input w-full min-h-16" value={form.note}
            onChange={(e) => set('note')(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}

/** Record an award. The cap is the server's rule; this form does not pre-empt it. */
function AwardModal({ budget, onClose, onDone }: {
  budget: IncentiveBudget
  onClose: () => void
  onDone: () => void
}) {
  const kinds = useLoad(incentiveKinds, [])
  const reasons = useLoad(awardReasons, [])
  const recipients = useLoad(recipientKinds, [])
  const bases = useLoad(valueBases, [])
  const channels = useLoad(deliveryChannels, [])
  const cells = useLoad(
    () => listCells({ electionId: budget.electionId, status: 'ACTIVE' }),
    [budget.electionId])
  const [cellId, setCellId] = useState('')
  const [membershipId, setMembershipId] = useState('')
  const members = useLoad(
    () => (cellId ? listCellMembers(cellId) : Promise.resolve([])), [cellId])
  const cellRows = cells.data ?? []
  const cell = cellRows.find((c) => c.id === cellId)
  const [form, setForm] = useState({
    recipientKindCode: '', incentiveKindCode: '', awardReasonCode: '',
    valueBasisCode: '', deliveryChannelCode: '',
    quantity: '1', unitValue: '',
    deliveredOn: new Date().toISOString().slice(0, 10),
    externalReference: '', note: '',
  })
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const set = (k: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [k]: v }))

  const amount = Number(form.quantity || 0) * Number(form.unitValue || 0)

  const submit = async () => {
    setBusy(true); setError(null)
    try {
      await recordAward(budget.id, {
        electionId: budget.electionId,
        budgetId: budget.id,
        recipientKindCode: form.recipientKindCode,
        // Who actually received it. Without these the row records a recipient KIND and
        // no recipient, which is a payment nobody can trace to a person.
        cellId,
        memberId: form.recipientKindCode === 'MEMBER' ? membershipId : null,
        stationId: cell?.stationId ?? null,
        // turnout_incentive_award.unit_path is NOT NULL and GIST-indexed for scope. The
        // cell's own path is the correct value; a placeholder would file the spend under
        // the wrong county in every report that reads it.
        unitPath: cell?.unitPath ?? '',
        incentiveKindCode: form.incentiveKindCode,
        awardReasonCode: form.awardReasonCode,
        quantity: Number(form.quantity),
        unitValue: Number(form.unitValue),
        currency: budget.currency,
        valueBasisCode: form.valueBasisCode,
        deliveryChannelCode: form.deliveryChannelCode,
        deliveredOn: form.deliveredOn,
        externalReference: form.externalReference || null,
        note: form.note || null,
      })
      onDone()
    } catch (e) { setError(explain(e)); setBusy(false) }
  }

  const ready = form.recipientKindCode && form.incentiveKindCode && form.awardReasonCode
    && form.valueBasisCode && form.deliveryChannelCode && form.unitValue && form.deliveredOn
    && cell && (form.recipientKindCode !== 'MEMBER' || membershipId)

  const select = (
    k: keyof typeof form, text: string, codes: string[] | null, loading: boolean,
    hint?: string,
  ) => (
    <Field label={text} required hint={hint}>
      <select className="erp-input h-9 w-full" value={form[k]} disabled={loading}
        onChange={(e) => set(k)(e.target.value)}>
        <option value="">Choose…</option>
        {(codes ?? []).map((c) => <option key={c} value={c}>{label(c)}</option>)}
      </select>
    </Field>
  )

  return (
    <Modal open onClose={onClose} width="lg" title={`Record an award — ${budget.name}`}
      description={`${Number(budget.remainingAmount).toLocaleString()} ${budget.currency} remaining of ${Number(budget.capAmount).toLocaleString()}.`}
      footer={(
        <>
          <button type="button" className="erp-btn h-9" onClick={onClose}>Cancel</button>
          <button type="button" className="erp-btn erp-btn-primary h-9"
            disabled={busy || !ready} onClick={submit}>
            {busy ? 'Recording…' : 'Record'}
          </button>
        </>
      )}>
      <ErrorStrip message={error} />
      <div className="space-y-3">
        <Field label="Cell" required
          hint="The award is recorded against this cell, and takes its scope path from it.">
          <select className="erp-input h-9 w-full" value={cellId}
            disabled={cells.loading}
            onChange={(e) => { setCellId(e.target.value); setMembershipId('') }}>
            <option value="">Choose a cell…</option>
            {cellRows.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label ?? c.code}{c.stationName ? ` — ${c.stationName}` : ''}
              </option>
            ))}
          </select>
        </Field>
        {select('recipientKindCode', 'Recipient kind', recipients.data, recipients.loading,
          'CELL records it against the cell as a whole; MEMBER names one person.')}
        {form.recipientKindCode === 'MEMBER' && (
          <Field label="Member" required
            hint="Who received it. A cash hand-over with no named recipient is the least auditable row in this feature.">
            <select className="erp-input h-9 w-full" value={membershipId}
              disabled={!cellId || members.loading}
              onChange={(e) => setMembershipId(e.target.value)}>
              <option value="">Choose a member…</option>
              {(members.data ?? []).map((m) => (
                <option key={m.id} value={m.id}>{m.displayName}</option>
              ))}
            </select>
          </Field>
        )}
        {select('incentiveKindCode', 'Incentive kind', kinds.data, kinds.loading)}
        {select('awardReasonCode', 'Reason', reasons.data, reasons.loading)}
        {select('valueBasisCode', 'Value basis', bases.data, bases.loading)}
        {select('deliveryChannelCode', 'Delivery channel', channels.data, channels.loading)}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Quantity" required>
            <input className="erp-input h-9 w-full" type="number" min="1" value={form.quantity}
              onChange={(e) => set('quantity')(e.target.value)} />
          </Field>
          <Field label={`Unit value (${budget.currency})`} required>
            <input className="erp-input h-9 w-full" type="number" min="0" value={form.unitValue}
              onChange={(e) => set('unitValue')(e.target.value)} />
          </Field>
        </div>
        {/*
           Shown, not enforced. If this exceeds the remaining balance the server refuses
           with turnout/budget-cap-exceeded and that sentence is what the operator reads —
           a disabled button would not say which rule stopped them.
        */}
        <p className="text-erp-xs text-muted-foreground">
          Amount: <span className="tabular-nums font-medium">
            {amount.toLocaleString()} {budget.currency}
          </span>
          {amount > Number(budget.remainingAmount) && (
            <span className="text-amber-700">
              {' '}— more than this budget has left. The server will refuse it and say so.
            </span>
          )}
        </p>
        <Field label="Delivered on" required>
          <DatePicker value={form.deliveredOn} onChange={set('deliveredOn')} />
        </Field>
        <Field label="External reference"
          hint="The M-Pesa code, voucher number or bank reference. What an auditor traces.">
          <input className="erp-input h-9 w-full" value={form.externalReference}
            onChange={(e) => set('externalReference')(e.target.value)} />
        </Field>
        <Field label="Note">
          <textarea className="erp-input w-full min-h-16" value={form.note}
            onChange={(e) => set('note')(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}

/** Reversal writes a new negative row. The original stays exactly where it is. */
function ReverseModal({ award, onClose, onDone }: {
  award: IncentiveAward
  onClose: () => void
  onDone: () => void
}) {
  const reasons = useLoad(reversalReasons, [])
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (!reason) return
    setBusy(true); setError(null)
    try { await reverseAward(award.id, reason, note || null); onDone() }
    catch (e) { setError(explain(e)); setBusy(false) }
  }

  return (
    <Modal open onClose={onClose} title="Reverse this award"
      description={`${Number(award.amount).toLocaleString()} ${award.currency} · ${label(award.incentiveKindCode)}`}
      footer={(
        <>
          <button type="button" className="erp-btn h-9" onClick={onClose}>Cancel</button>
          <button type="button" className="erp-btn erp-btn-primary h-9"
            disabled={busy || !reason} onClick={submit}>
            {busy ? 'Reversing…' : 'Reverse'}
          </button>
        </>
      )}>
      <ErrorStrip message={error} />
      <div className="space-y-3">
        <Field label="Reason" required
          hint="Writes a new row with a negative amount. The original award is not touched.">
          <select className="erp-input h-9 w-full" value={reason}
            disabled={reasons.loading}
            onChange={(e) => setReason(e.target.value)}>
            <option value="">Choose…</option>
            {(reasons.data ?? []).map((r) => (
              <option key={r} value={r}>{label(r)}</option>
            ))}
          </select>
        </Field>
        <Field label="Note">
          <textarea className="erp-input w-full min-h-16" value={note} maxLength={512}
            onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}
